import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Clock, Calendar, PhoneCall, Baby, Shield, Users, Star, CheckCircle, IdCard, Car, FileCheck, Heart, Eye, AlertTriangle, Play } from 'lucide-react';
import SEO from '../components/SEO';

const ForFamilies = () => {
  const appUrl = process.env.REACT_APP_EXTERNAL_APP_URL || 'https://aupairshare.base44.app';

  const handleAppRedirect = () => {
    window.open(appUrl, '_blank');
  };

  const whoThisIsFor = [
    'Working parents needing reliable backup support',
    'Families requiring 5–20 hours per week, not 160+ hours',
    'Dual-income households with unpredictable schedules',
    'Single parents balancing work and childcare alone',
    'Families needing occasional, same-day, or emergency care',
    'Parents seeking professional care without monthly salaries or contracts'
  ];

  const trustFramework = [
    {
      category: 'Identity & Safety',
      items: ['Valid ID verification', 'Police clearance (renewed regularly)']
    },
    {
      category: 'Vehicle & Transport Safety',
      items: ['Roadworthy vehicle inspection by an approved third party', 'Valid driver\'s licence and insurance verification']
    },
    {
      category: 'Qualifications & Experience',
      items: ['Qualification or degree verification where applicable', 'Confirmation of childcare experience', 'Reference checks']
    },
    {
      category: 'Values & Personality Screening',
      items: ['Psychometric assessments assessing responsibility, values alignment, emotional maturity, boundaries and reliability', 'Results reviewed as part of the vetting process']
    }
  ];

  const bookingTypes = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Scheduled Bookings',
      items: ['Regular school pickups', 'After-school activities', 'Recurring childcare needs']
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Same-Day Support',
      items: ['Unexpected work commitments', 'Schedule changes', 'Last-minute activities']
    },
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: 'Emergency Collections',
      items: ['Traffic delays', 'Late meetings', 'Urgent, unplanned situations']
    },
    {
      icon: <Baby className="w-8 h-8" />,
      title: 'Babysitting',
      items: ['Evening plans', 'Social events', 'Personal appointments']
    }
  ];

  const whatAuPairsDo = [
    'Provide safe transportation in their own vehicles',
    'Engage with children during care',
    'Assist with homework and activities',
    'Offer attentive, responsible supervision',
    'Follow your family\'s specific instructions',
    'Communicate through the app'
  ];

  const whatTheyDontDo = [
    'Household chores unrelated to childcare',
    'Full-time live-in arrangements',
    'Medical care or administration of medication',
    'Long-distance travel outside agreed areas'
  ];

  const profileFeatures = [
    'A verified biography',
    'Experience and qualifications',
    'A short introduction video',
    'Ratings and feedback from other families'
  ];

  const parentControls = [
    'Choose preferred au pairs',
    'Save favourites for future bookings',
    'Block au pairs they do not wish to book again',
    'Request the same au pair where availability allows'
  ];

  const accountabilityFeatures = [
    'Two-way ratings and reviews',
    'Ongoing monitoring of conduct and reliability',
    'Immediate action if safety or boundaries are compromised',
    'Incident reporting and support systems'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="For Families"
        description="Trusted, flexible childcare for families in South Africa. Vetted au pairs, professional screening, and you only pay for hours used. Johannesburg, Pretoria, Cape Town & Durban."
        keywords="family childcare, au pair for families, trusted childcare, vetted nanny, part-time childcare, Johannesburg, Cape Town"
        path="/families"
      />

      {/* SECTION 1: Hero - Opening Reassurance */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-50 opacity-50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Childcare You Can Trust — Even When You Need It at Short Notice
              </h1>
              <div className="text-lg text-slate-700 space-y-4 leading-relaxed">
                <p>
                  Finding childcare isn't just about availability. It's about <strong>trust</strong>.
                </p>
                <p>
                  Au Pair Share was created for families who need flexible, on-demand childcare without compromising on safety, values, or quality of care. Whether planned in advance or needed unexpectedly, we support families with reliable care designed around real life.
                </p>
              </div>
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg"
                data-testid="hero-cta-btn"
              >
                Find an Au Pair
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1635770618602-1ec67f29a1af" 
                alt="Trusted au pair with child"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Professional Care Without the Full-Time Cost */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                The Same Standard of Care — Without the Commitment
              </h2>
            </div>
            <div className="text-lg text-slate-700 space-y-4 leading-relaxed mb-8">
              <p>
                At Au Pair Share, families gain access to the same high-quality care expected from a professional au pair, without the cost, complexity, or commitment of full-time arrangements.
              </p>
              <p>
                Our carefully selected au pairs provide reliable, nurturing, and responsible childcare, available exactly when life demands it. You book only what you need — and pay only for the hours used.
              </p>
            </div>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8">
              <h3 className="font-semibold text-slate-900 mb-4 text-lg">What this means for families:</h3>
              <div className="space-y-3 text-slate-800">
                <p className="flex items-center gap-3">
                  <span className="text-green-600 text-xl">✓</span> No monthly salaries
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-green-600 text-xl">✓</span> No long-term contracts
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-green-600 text-xl">✓</span> No paying for time you don't need
                </p>
              </div>
              <p className="mt-6 font-semibold text-slate-900 text-lg">
                Professional childcare — on your terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: Who This Service Is For */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Who This Service Is For
              </h2>
              <p className="text-lg text-slate-600">
                Au Pair Share is designed for families who need flexibility without compromise, including:
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 lg:p-10 shadow-sm">
              <div className="grid md:grid-cols-2 gap-5">
                {whoThisIsFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-slate-700 rounded-full p-1 mt-1.5 flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: Why Au Pair Share Is Different */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              More Than a Match — A Shared Responsibility
            </h2>
            <div className="text-lg text-slate-700 space-y-4 leading-relaxed">
              <p>
                Traditionally, parents carry the full burden of interviewing, screening, and assessing suitability — often without formal tools or training.
              </p>
              <p>
                <strong>Au Pair Share was designed to change that.</strong>
              </p>
              <p>
                Every au pair on our platform is vetted through a structured, multi-layered screening process, allowing families to rely on professional, standardised checks, rather than instinct alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: The Au Pair Share Trust Framework */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                The Au Pair Share Trust Framework™
              </h2>
              <p className="text-xl text-slate-300">
                Your Child's Safety Is Our Priority
              </p>
              <p className="text-slate-400 mt-4">
                All au pairs are screened before being accepted onto the platform.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {trustFramework.map((section, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="font-semibold text-white mb-4 text-lg">{section.category}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-300">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="text-center text-slate-400 mt-10 text-lg">
              This level of screening is rarely available to families sourcing childcare independently.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6: Getting to Know Your Au Pair */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Getting to Know Your Au Pair — Without Interviews
              </h2>
              <p className="text-lg text-slate-600">
                Transparency Without the Burden
              </p>
            </div>
            <div className="text-lg text-slate-700 space-y-4 leading-relaxed mb-8 text-center">
              <p>
                While families no longer need to conduct full interviews, transparency remains essential.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="font-semibold text-slate-900 mb-6 text-center">Each au pair profile includes:</h3>
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {profileFeatures.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-center text-slate-600 mt-8">
                This allows parents to make informed choices while relying on consistent professional screening.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: You're Always in Control */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                You're Always in Control
              </h2>
              <p className="text-lg text-slate-600">
                Families retain choice — never obligation.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-6">Parents can:</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {parentControls.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-center text-slate-600 mt-8 font-medium">
                Au Pair Share supports flexibility without removing parental control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: Types of Support We Offer */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Types of Support We Offer
            </h2>
            <p className="text-lg text-slate-600">
              From Planned Care to Emergency Support
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {bookingTypes.map((type, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 space-y-4">
                  <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center text-slate-700">
                    {type.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{type.title}</h3>
                  <ul className="space-y-2">
                    {type.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                        <div className="w-1 h-1 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-slate-700 font-medium mt-10 text-lg">
            Trusted care, when you need it most.
          </p>
        </div>
      </section>

      {/* SECTION 9: What Our Au Pairs Do (And Don't Do) */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-12 text-center">
              What Our Au Pairs Do (And Don't Do)
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 rounded-xl p-8 space-y-6 border border-green-200">
                <h3 className="text-2xl font-semibold text-green-900">What They Do</h3>
                <ul className="space-y-3">
                  {whatAuPairsDo.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-100 rounded-xl p-8 space-y-6">
                <h3 className="text-2xl font-semibold text-slate-900">What They Don't Do</h3>
                <ul className="space-y-3">
                  {whatTheyDontDo.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-slate-400 flex-shrink-0 mt-0.5"></div>
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10: Pay Only for What You Use */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Pay Only for What You Use
              </h2>
              <p className="text-lg text-slate-600">
                A Smarter Alternative to Full-Time Au Pairs
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">R15,000+</div>
                  <div className="text-sm text-slate-500 mb-4 font-medium">Full-Time Au Pair Monthly Cost</div>
                  <ul className="text-left text-sm text-slate-600 space-y-2">
                    <li>• Salary</li>
                    <li>• Accommodation</li>
                    <li>• Meals & transport</li>
                    <li>• Leave & benefits</li>
                    <li>• 160+ hours per month</li>
                  </ul>
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-5xl font-bold text-green-600">→</div>
                </div>
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="text-3xl font-bold text-green-700 mb-2">Au Pair Share</div>
                  <ul className="text-left text-sm text-slate-700 space-y-2 mt-4">
                    <li>• Hourly rates only</li>
                    <li>• No accommodation costs</li>
                    <li>• No leave management</li>
                    <li>• Use as little as 5–20 hours per month</li>
                  </ul>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Example Family Usage:</h3>
                <div className="bg-slate-50 rounded-lg p-6 max-w-md mx-auto">
                  <ul className="text-slate-700 space-y-2">
                    <li>• 3 school pickups per week</li>
                    <li>• Weekend babysitting</li>
                    <li>• Occasional emergency cover</li>
                    <li className="font-semibold text-slate-900 pt-3 border-t border-slate-200 mt-3">≈ 10–15 hours per month</li>
                  </ul>
                </div>
                <p className="text-center text-slate-700 font-medium mt-6">
                  You pay only for the hours used — nothing more.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 11: Accountability & Ongoing Safety */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Accountability & Ongoing Safety
              </h2>
              <p className="text-lg text-slate-600">
                Built-In Accountability
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {accountabilityFeatures.map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-slate-600 mt-8 font-medium">
              Trust is maintained through transparency and consequences.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 12: Designed by a Parent */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-slate-700 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Designed by a Parent, Built by Professionals
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Au Pair Share was founded by a registered clinical psychologist and parent, with firsthand understanding of child safety, family dynamics, and real-world parenting demands.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mt-4 font-medium">
              Every safeguard on this platform reflects the standard we would expect for our own children.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 13: Closing Call to Action */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Flexible childcare should never mean compromised standards.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
                data-testid="cta-find-aupair-btn"
              >
                Find an Au Pair
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.location.href = '/safety'}
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                data-testid="cta-screening-btn"
              >
                How Our Screening Works
              </Button>
            </div>
            <p className="text-slate-400 text-lg italic mt-8">
              "I'm not handing my child to a stranger — I'm sharing responsibility with a system designed to protect my family, while only paying for what I truly need."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForFamilies;
